import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { DigitalScoreRing, DigitalTierBadge } from '../components/DigitalScore';
import { Button } from '../components/ui/button';
import { Search, ArrowRight, Building2, Wifi, AlertTriangle, TrendingUp, ExternalLink, Share2 } from 'lucide-react';
import ReportGenerator from '../components/ReportGenerator';
import DistrictShare from '../components/DistrictShare';

export default function HomePage() {
  const [overview, setOverview] = useState(null);
  const [postcodes, setPostcodes] = useState([]);
  const [darkBusinesses, setDarkBusinesses] = useState([]);
  const [quickSearch, setQuickSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [ov, pc, dark] = await Promise.all([
          api.get('/analytics/overview'),
          api.get('/postcodes'),
          api.post('/businesses/search', { digital_tier: 'Digitally Invisible', limit: 5 })
        ]);
        setOverview(ov.data);
        setPostcodes(pc.data);
        setDarkBusinesses(dark.data.businesses || []);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const scopeLabel = overview?.scope && overview.scope !== 'tvca' ? overview.scope_name : 'Tees Valley';
  const stats = overview ? [
    { label: `Est. Businesses in ${scopeLabel}`, value: (overview.estimated_total_stockton_businesses || 16820).toLocaleString(), icon: Building2, color: 'text-info-blue' },
    { label: 'Tracked & Scored', value: (overview.tracked_and_scored || overview.total_businesses)?.toLocaleString(), icon: Search, color: 'text-ai-purple' },
    { label: 'Ultrafast Coverage', value: `${overview.ultrafast_coverage_pct || 95}%`, icon: Wifi, color: 'text-status-green' },
    { label: 'Digitally Invisible', value: `${overview.digitally_invisible_pct || 55}%`, icon: AlertTriangle, color: 'text-status-red' },
  ] : [];

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-8 space-y-8" data-testid="home-page">
      {/* Hero */}
      <div className="glass-panel p-6 lg:p-8 animate-fade-in">
        <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
          {scopeLabel}<br/>
          <span className="text-brand-orange">Digital Register</span>
        </h1>
        <p className="font-body text-base text-ink/80 mt-4 max-w-3xl leading-relaxed font-medium">
          The UK's first postcode-level business intelligence platform connecting broadband coverage, digital adoption, and community deprivation — so local authorities know exactly where to intervene and what return to expect.
        </p>
        <p className="font-body text-sm text-ink/60 mt-3 max-w-3xl leading-relaxed">
          {scopeLabel} has {overview?.ultrafast_coverage_pct || 95}% ultrafast broadband coverage — well above the England average of {overview?.england_ultrafast_average_pct || 90}%. Yet {overview?.digitally_invisible_pct || 55}% of its registered businesses have zero meaningful digital presence, concentrated in the most deprived postcodes. The infrastructure exists. The economic consequence does not.
        </p>
        <div className="flex gap-3 mt-6 flex-wrap">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" strokeWidth={1.5} />
            <input
              data-testid="home-quick-search"
              value={quickSearch}
              onChange={e => setQuickSearch(e.target.value)}
              placeholder="Search by name, postcode, sector, or CH number…"
              className="w-full pl-9 pr-4 py-2.5 rounded-[4px] border border-ink/15 bg-white font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
              onKeyDown={e => e.key === 'Enter' && quickSearch && (window.location.href = `/search?q=${quickSearch}`)}
            />
          </div>
          <Link to={quickSearch ? `/search?q=${quickSearch}` : '/search'}>
            <Button data-testid="home-search-btn" className="bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px] px-4 transition-all hover:-translate-y-[1px]">
              Search <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <ReportGenerator />
        </div>
      </div>

      {/* Stats bar */}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          {stats.map((s, i) => (
            <div key={i} className="solid-card p-4 lg:p-5" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon className={`w-4 h-4 ${s.color}`} strokeWidth={1.5} />
                <span className="font-body text-xs uppercase tracking-[0.1em] text-ink/50">{s.label}</span>
              </div>
              <span className="font-display text-2xl lg:text-3xl font-extrabold text-ink">{s.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* District tiles */}
      <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
        <h2 className="font-display text-2xl font-extrabold text-ink mb-4">Postcode Districts</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {postcodes.map(pc => {
            const cat1 = pc.cat1_trading_invisible || 0;
            const cat2 = pc.cat2_shell_companies || 0;
            const cat3 = pc.cat3_dissolved_winding_down || 0;
            const totalInv = (cat1 + cat2 + cat3) || (pc.digitally_invisible || 0);
            const invPct = pc.total_businesses ? (totalInv / pc.total_businesses) * 100 : null;
            return (
            <div key={pc.district} className="relative">
              <Link to={`/search?district=${pc.district}`} data-testid={`district-tile-${pc.district}`}>
                <div className="solid-card p-4 hover:-translate-y-[1px] transition-all cursor-pointer group">
                  <div className="flex items-center justify-between mb-2 pr-9">
                    <span className="font-display text-lg font-extrabold text-ink">{pc.district}</span>
                    {(() => { const risk = (pc.connectivity_risk || '').toLowerCase();
                      return (
                    <span className={`text-xs font-body px-1.5 py-0.5 rounded-[4px] capitalize ${
                      risk === 'low' ? 'bg-status-green/10 text-status-green' :
                      risk === 'medium' ? 'bg-status-amber/10 text-status-amber' :
                      'bg-status-red/10 text-status-red'
                    }`}>{risk || '—'}</span>
                      ); })()}
                  </div>
                  <p className="font-body text-xs text-ink/50 mb-3">{pc.name}</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-body">
                      <span className="text-ink/50">Est. Businesses</span>
                      <span className="text-ink font-medium">{pc.total_businesses?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs font-body">
                      <span className="text-ink/50">CH Registered</span>
                      <span className="text-ai-purple font-medium">{pc.registered_in_db?.toLocaleString() || '—'}</span>
                    </div>
                    <div className="flex justify-between text-xs font-body">
                      <span className="text-ink/50">Adoption</span>
                      <span className="text-status-green font-medium">{pc.db_adoption_rate ?? pc.adoption_rate ?? 0}%</span>
                    </div>
                    <div className="flex justify-between text-xs font-body">
                      <span className="text-ink/50">Avg Score</span>
                      <span className="text-ink font-medium">{pc.db_avg_score ?? pc.avg_digital_score ?? 0}/5</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-ink/5 rounded-full mt-3 overflow-hidden">
                    <div className="h-full rounded-full bg-status-green" style={{ width: `${pc.db_adoption_rate ?? pc.adoption_rate ?? 0}%` }} />
                  </div>
                </div>
              </Link>
              <DistrictShare district={pc.district} name={pc.name} invisiblePct={invPct} />
            </div>
            );
          })}
        </div>
      </div>

      {/* Dark businesses callout */}
      {darkBusinesses.length > 0 && (
        <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="solid-card p-5 border-l-4 border-l-status-red">
            <h3 className="font-display text-xl font-extrabold text-ink mb-1">Digitally Invisible Businesses</h3>
            <p className="font-body text-xs text-ink/50 mb-4">These businesses have no verified digital presence — they cannot be found online.</p>
            <div className="space-y-2">
              {darkBusinesses.map(biz => (
                <Link key={biz.id} to={`/business/${biz.id}`} className="flex items-center justify-between p-2 rounded-[4px] hover:bg-ink/3 transition-all">
                  <div>
                    <span className="font-body text-sm text-ink">{biz.company_name}</span>
                    <span className="font-body text-xs text-ink/40 ml-2">{biz.postcode_district} · {biz.sector}</span>
                  </div>
                  <DigitalTierBadge tier={biz.digital_tier} />
                </Link>
              ))}
            </div>
            <Link to="/search?tier=Digitally+Invisible">
              <Button variant="outline" size="sm" className="mt-3 font-body text-xs rounded-[4px]">
                View All Invisible Businesses <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Footer attribution */}
      <div className="text-center py-4 font-body text-xs text-ink/30 leading-relaxed">
        Data sources: Companies House (OGL v3.0) · ONS IDBR · Ofcom Connected Nations · Google Places API<br />
        Digital footprint assessments are point-in-time checks. Nexintell · <a href="/privacy" className="underline">Privacy Policy</a>
      </div>
    </div>
  );
}
